import React, { type ReactNode } from 'react';
import {
  Cell,
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../primitives/Table';
import { Checkbox } from '../primitives/Checkbox';
import { cn } from '../../cn';
import { useResourceKit } from '@resource-kit/core/react';
import type { ListCardRenderer, ResourceColumnMeta } from '@resource-kit/controllers/react';
import { EmptyState } from './EmptyState';
import { DataTablePagination, type PaginationModel } from './DataTablePagination';
import { DataTableCard } from './DataTableCards';
import { LIST_LAYOUT, type ListLayout } from './useListLayout';

const SERVER_PAGE_SIZES = [10, 20, 50, 100] as const;
const CLIENT_PAGE_SIZES = [10, 20, 30, 40, 50] as const;

/**
 * Props for the DataTable component.
 * @template TData The type of data displayed in the table.
 * @template TValue The type of value for columns.
 */
export interface DataTableProps<TData, TValue> {
  /** Column definitions for the table. */
  columns: ColumnDef<TData, TValue>[];
  /** Array of data to display. */
  data: TData[];
  /**
   * Backend pagination configuration.
   * If provided, enables server-side pagination.
   */
  pagination?: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    total: number;
    pageSize: number;
    /**
     * Page-size handler. When omitted, the rows-per-page Select is not
     * rendered at all — a page whose list endpoint owns its own page size has
     * no size control rather than a dead one that swallows input.
     */
    onPageSizeChange?: (size: number) => void;
  };
  /** Whether direct consumers use client-side pagination. Defaults to true. */
  clientPagination?: boolean;
  /** Whether the table is in a loading state. */
  loading?: boolean;
  /** Callback when a sortable column header is clicked. */
  onSort?: (field: string) => void;
  /** Currently sorted field. */
  sortField?: string;
  /** Current sort order ('asc' or 'desc'). */
  sortOrder?: 'asc' | 'desc';
  /** Callback when a row is clicked. */
  onRowClick?: (row: TData) => void;
  /** Whether rows can be selected. */
  selectable?: boolean;
  /**
   * Callback when row selection changes. Excluded from the emit effect's
   * deps on purpose: the caller passes a stable setter (e.g. React's
   * `setSelectedRows`), so re-subscribing on every render would only add
   * churn without changing behaviour, and there is no stale-closure risk.
   */
  onSelectionChange?: (selected: TData[]) => void;
  /** Bump to force-clear the internal row selection after a bulk action. */
  selectionResetKey?: number;
  /**
   * Stable string describing the current query context (page / sort / search /
   * filter). Internal row selection is cleared whenever this key changes — i.e.
   * when the operator navigates to a different slice of data. A background
   * refetch of the SAME page leaves this key unchanged, so id-keyed selection
   * (`getRowId`) survives it. This is the page-local selection contract.
   */
  selectionContextKey?: string;
  /** Stable row identity; keys selection on business id rather than row index. */
  getRowId?: (row: TData) => string;
  /**
   * Controlled column visibility. When provided, the caller owns the state
   * (e.g. ListView's persisted show/hide menu) and the table renders it
   * verbatim, reporting changes through `onColumnVisibilityChange`. When
   * omitted, the table falls back to its own internal state seeded from each
   * column's `meta.hidden` — preserving the behavior of direct DataTable users.
   */
  columnVisibility?: VisibilityState;
  /** Change callback for controlled `columnVisibility`. */
  onColumnVisibilityChange?: (next: VisibilityState) => void;
  /**
   * What to render in place of the default "no results" row (#1801).
   *
   * The framework contract (design §6.4) distinguishes "empty, unfiltered"
   * from "empty, filtered" — only the caller knows which one it is, and only
   * the caller can offer the CLEAR FILTERS way out. The table keeps its
   * generic fallback for direct consumers that have no such distinction.
   */
  emptyContent?: ReactNode;
  /**
   * Render the rows as a table (default) or as cards (#2680). Both forms read
   * the same table instance, so controls, column visibility, selection and
   * pagination are one state shown two ways.
   */
  layout?: ListLayout;
  /** A resource's own card layout; absent ⇒ the default card. Cards only. */
  renderCard?: ListCardRenderer<TData>;
  /**
   * Where a card's title row goes. When it answers a path the title row
   * navigates there; otherwise it falls back to `onRowClick`. Cards only —
   * the table row keeps `onRowClick`.
   */
  getRowHref?: (row: TData) => string | undefined;
}

/**
 * Memoized row / cell (#1796).
 *
 * The table sits under a page that re-renders for reasons the rows do not care
 * about — a pending flag, a drawer opening, a bulk-action guard. Without a memo
 * boundary every one of those rebuilt every cell of every row; with 1000
 * client-paginated rows that is the page's dominant render cost. Splitting the
 * row from the cell means a selection toggle re-renders one row's checkbox
 * without touching the cells beside it.
 *
 * Every input that can change what a row paints is therefore an explicit prop.
 * Two are easy to miss:
 *
 *   - `cells` is `row.getVisibleCells()`, computed by the caller. Its identity
 *     changes when column visibility changes, which is how hiding a column
 *     reaches a memoized row.
 *   - `language` is not read here at all: it is a memo key. Several resource
 *     cell renderers translate through the i18n singleton (`i18n.t(...)`),
 *     which is NOT a React subscription, so a memoized row that ignored the
 *     active language would keep the previous language's labels after a switch.
 */
interface DataTableRowProps<TData> {
  row: Row<TData>;
  cells: Cell<TData, unknown>[];
  isSelected: boolean;
  selectable: boolean;
  /** Stable identity — see the latest-ref wrapper in `DataTable`. */
  onRowClick?: (row: TData) => void;
  selectRowLabel: string;
  language: string;
}

function DataTableRowImpl<TData>({
  row,
  cells,
  isSelected,
  selectable,
  onRowClick,
  selectRowLabel,
  language,
}: DataTableRowProps<TData>) {
  return (
    <TableRow
      data-state={isSelected && 'selected'}
      className={cn(onRowClick && 'cursor-pointer')}
      onClick={() => onRowClick?.(row.original)}
    >
      {selectable && (
        <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={selectRowLabel}
          />
        </TableCell>
      )}
      {cells.map((cell) => (
        <DataTableCell key={cell.id} cell={cell} language={language} />
      ))}
    </TableRow>
  );
}

// `React.memo` erases the generic; the cast restores the call signature so
// callers keep inference. The default shallow prop comparison is exactly the
// contract above.
const DataTableRow = React.memo(DataTableRowImpl) as typeof DataTableRowImpl;

interface DataTableCellProps<TData> {
  cell: Cell<TData, unknown>;
  /** Memo key only — see `DataTableRowProps.language`. */
  language: string;
}

function DataTableCellImpl<TData>({ cell }: DataTableCellProps<TData>) {
  return <TableCell>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>;
}

const DataTableCell = React.memo(DataTableCellImpl) as typeof DataTableCellImpl;

export function DataTable<TData, TValue>({
  columns,
  data,
  // Backend pagination props
  pagination,
  clientPagination = true,
  loading: _loading = false,
  onSort,
  sortField,
  sortOrder,
  onRowClick,
  selectable = false,
  onSelectionChange,
  selectionResetKey,
  selectionContextKey,
  getRowId,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  emptyContent,
  layout = LIST_LAYOUT.table,
  renderCard,
  getRowHref,
}: DataTableProps<TData, TValue>) {
  const { t, i18n, format } = useResourceKit();
  // Callers pass a fresh `onRowClick` closure on every render (it captures the
  // page's current state), which would defeat the row memo. Route it through a
  // latest-ref so the row sees ONE identity for the table's lifetime while the
  // click still runs the current closure.
  const onRowClickRef = React.useRef(onRowClick);
  React.useEffect(() => {
    onRowClickRef.current = onRowClick;
  });
  const hasRowClick = onRowClick !== undefined;
  const handleRowClick = React.useMemo(
    () =>
      hasRowClick
        ? (row: TData) => {
            onRowClickRef.current?.(row);
          }
        : undefined,
    [hasRowClick],
  );
  const selectRowLabel = t('rk.table.selectRow');
  const [sorting, setSorting] = React.useState<SortingState>(() => {
    if (sortField) {
      return [{ id: sortField, desc: sortOrder === 'desc' }];
    }
    return [];
  });

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const defaultVisibility = React.useMemo<VisibilityState>(() => {
    const vis: VisibilityState = {};
    columns.forEach((col) => {
      const accessorKey = 'accessorKey' in col ? col.accessorKey : undefined;
      const id = typeof accessorKey === 'string' ? accessorKey : col.id;
      if (!id) return;
      // `meta.hidden` is the resource kit's own column metadata, which
      // `@resource-kit/controllers/react` merges into TanStack's `ColumnMeta`
      // with a `declare module`. Naming the type here is what puts that
      // declaration in THIS package's program: the boundary tsconfig sets
      // `types: []`, so nothing is ambient and an augmentation applies only
      // where the module declaring it is actually reached.
      const meta: ResourceColumnMeta<TData> | undefined = col.meta;
      if (meta?.hidden === true) vis[id] = false;
    });
    return vis;
  }, [columns]);
  // Internal fallback state for uncontrolled use (direct DataTable callers).
  // When the caller drives visibility (`controlledColumnVisibility` provided),
  // this state is inert and the controlled value/setter win.
  const [internalColumnVisibility, setInternalColumnVisibility] =
    React.useState<VisibilityState>(defaultVisibility);
  const isVisibilityControlled = controlledColumnVisibility !== undefined;
  const columnVisibility = isVisibilityControlled
    ? controlledColumnVisibility
    : internalColumnVisibility;
  const [rowSelection, setRowSelection] = React.useState({});

  // Sync props to local sorting state for server-side mode
  React.useEffect(() => {
    if (sortField) {
      setSorting([{ id: sortField, desc: sortOrder === 'desc' }]);
    } else {
      setSorting([]);
    }
  }, [sortField, sortOrder]);

  const useManual = !!pagination || !!onSort;

  const table = useReactTable({
    data,
    columns,
    ...(getRowId ? { getRowId } : {}),
    getCoreRowModel: getCoreRowModel(),
    ...(useManual
      ? {
          manualPagination: !!pagination,
          manualSorting: true,
          manualFiltering: !!pagination,
        }
      : {
          ...(clientPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
          getSortedRowModel: getSortedRowModel(),
          getFilteredRowModel: getFilteredRowModel(),
        }),
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      setSorting(newSorting);

      if (onSort && newSorting.length > 0) {
        const { id } = newSorting[0];
        onSort(id as string);
      }
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: (updaterOrValue) => {
      const next =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibility) : updaterOrValue;
      if (isVisibilityControlled) {
        onColumnVisibilityChange?.(next);
      } else {
        setInternalColumnVisibility(next);
      }
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: selectable,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  // Selection is page-local: clear the internal row selection when the query
  // CONTEXT changes (page / sort / search / filter), NOT on every `data`-ref
  // change. A background refetch of the same page (refetchOnWindowFocus)
  // produces a new `data` ref but the SAME context key, so id-keyed selection
  // (`getRowId`) survives it — which is the whole point of `getRowId`.
  // Initialised to the current key so the first render is a no-op (mirrors the
  // `prevResetKeyRef` pattern below) and can't loop. StrictMode-safe: a
  // re-init lands on the same value and the guard short-circuits.
  const prevContextKeyRef = React.useRef(selectionContextKey);
  React.useEffect(() => {
    if (prevContextKeyRef.current === selectionContextKey) return;
    prevContextKeyRef.current = selectionContextKey;
    setRowSelection({});
  }, [selectionContextKey]);

  // Emit selected rows whenever selection changes. Selection is the only thing
  // that drives the emitted rows (context changes clear it via the effect
  // above), so depending on `rowSelection` alone is sufficient and can't
  // diverge.
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      onSelectionChange?.([]);
      return;
    }
    // `onSelectionChange` / `table` intentionally omitted from deps (see prop JSDoc).
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    onSelectionChange?.(table.getSelectedRowModel().rows.map((r) => r.original));
  }, [rowSelection]);

  // Parent-driven clear after a bulk action.
  const prevResetKeyRef = React.useRef(selectionResetKey);
  React.useEffect(() => {
    if (selectionResetKey === undefined) return;
    if (prevResetKeyRef.current === selectionResetKey) return;
    prevResetKeyRef.current = selectionResetKey;
    setRowSelection({});
  }, [selectionResetKey]);

  // Selection switched off (a phone, #2680): drop what was ticked, so the
  // emit effect above reports `[]` and no bulk action keeps acting on rows
  // the operator can no longer see as selected.
  const prevSelectableRef = React.useRef(selectable);
  React.useEffect(() => {
    if (prevSelectableRef.current === selectable) return;
    prevSelectableRef.current = selectable;
    // Keep the same object when nothing is ticked: a fresh `{}` would re-run
    // the emit effect and report an extra, redundant `[]`.
    if (!selectable) setRowSelection((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [selectable]);

  // Both pagination modes project onto ONE pager (see DataTablePagination).
  // Server mode compares against the page count, client mode asks the table.
  // The "an empty list is one empty page, never zero" floor lives in
  // DataTablePagination — the single component BOTH branches pass through — so
  // the raw count is forwarded here unfloored. `<` — not `!==` — is what "there
  // is a page after this one" means; `!==` was also true for any out-of-range
  // page, which left Next/Last live on an empty list.
  // `totalPages` is a required number and both readers sit inside the
  // `pagination` branch, so the `: 0` arm is never read.
  // Backward reads the same way: `> 1`, not `!== 1`. The two agree on every
  // page the server can hand us — `parseListQuery` floors the page at 1 through
  // `clampPage` (`backend/src/platform/http/list-query.ts::clampPage`) — so this is
  // the pair spelling one idea one way, not a fix for a reachable bug.
  const serverPageCount = pagination ? pagination.totalPages : 0;
  const paginationModel: PaginationModel | null = pagination
    ? {
        page: pagination.currentPage,
        pageCount: serverPageCount,
        pageSize: pagination.pageSize,
        pageSizeOptions: SERVER_PAGE_SIZES,
        onPageChange: pagination.onPageChange,
        onPageSizeChange: pagination.onPageSizeChange,
        canPreviousPage: pagination.currentPage > 1,
        canNextPage: pagination.currentPage < serverPageCount,
        // The singular/plural rows are named literally: the catalog-coverage
        // gate credits a `_one` row only by its own name, not through a base.
        caption: t(
          pagination.total === 1 ? 'rk.table.totalRecords_one' : 'rk.table.totalRecords_other',
          { total: format.formatNumber(pagination.total) },
        ),
      }
    : clientPagination
      ? {
          page: table.getState().pagination.pageIndex + 1,
          pageCount: table.getPageCount(),
          pageSize: table.getState().pagination.pageSize,
          pageSizeOptions: CLIENT_PAGE_SIZES,
          onPageChange: (page) => table.setPageIndex(page - 1),
          onPageSizeChange: (size) => table.setPageSize(size),
          canPreviousPage: table.getCanPreviousPage(),
          canNextPage: table.getCanNextPage(),
          caption: t('rk.table.rowsSelected', {
            count: table.getFilteredSelectedRowModel().rows.length,
          }),
        }
      : null;

  const emptyFallback = emptyContent ?? (
    <EmptyState title={t('rk.table.noResultsFound')} className="py-0" />
  );

  if (layout === LIST_LAYOUT.cards) {
    // No select-all here: the cards have no header row to carry it. An
    // operator who wants every row switches back to the table.
    const rows = table.getRowModel().rows;
    return (
      <div className="w-full space-y-4">
        {rows.length ? (
          <div
            data-testid="data-table-cards"
            className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(320px,100%),1fr))]"
          >
            {rows.map((row) => (
              <DataTableCard
                key={row.id}
                row={row}
                cells={row.getVisibleCells()}
                isSelected={row.getIsSelected()}
                selectable={selectable}
                href={getRowHref?.(row.original)}
                onOpen={handleRowClick}
                renderCard={renderCard}
                selectRowLabel={selectRowLabel}
                language={i18n.language}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-md border bg-card p-6 text-center">{emptyFallback}</div>
        )}
        {paginationModel && <DataTablePagination model={paginationModel} />}
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border bg-card overflow-x-auto overflow-y-clip">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {selectable && (
                  <TableHead className="w-12">
                    <Checkbox
                      checked={table.getIsAllPageRowsSelected()}
                      onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                      aria-label={t('rk.table.selectAll')}
                    />
                  </TableHead>
                )}
                {headerGroup.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  const ariaSort =
                    sorted === 'asc'
                      ? 'ascending'
                      : sorted === 'desc'
                        ? 'descending'
                        : header.column.getCanSort()
                          ? 'none'
                          : undefined;
                  return (
                    <TableHead key={header.id} aria-sort={ariaSort}>
                      {header.isPlaceholder ? null : (
                        <div className={cn('flex items-center space-x-2')}>
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table
                .getRowModel()
                .rows.map((row) => (
                  <DataTableRow
                    key={row.id}
                    row={row}
                    cells={row.getVisibleCells()}
                    isSelected={row.getIsSelected()}
                    selectable={selectable}
                    onRowClick={handleRowClick}
                    selectRowLabel={selectRowLabel}
                    language={i18n.language}
                  />
                ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="h-24 text-center"
                >
                  {emptyFallback}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {paginationModel && <DataTablePagination model={paginationModel} />}
    </div>
  );
}
